/**
 * P280 T0201 — the PREPARATION GATES seam.
 *
 * The menu asks this BEFORE it opens (SD-2): G1 the entity exists · G2 a FULL read is on record ·
 * G3 the rules are minted as a kernel a bot can read. Every gate comes back with the ONE action
 * that clears it, because `MR0u` says a gate that can only say yes is not a gate — and the mirror
 * of that is a refusal that says only "no", which moves the work back onto whoever asked.
 *
 * WHY THE ANSWER IS READ, NOT RE-DERIVED HERE. Each gate is computed server-side from real state
 * (`services/pitch_gates_service.py`): the entity row, the `_scrape` run record T0103 writes on
 * every run, and the rule kernel the checker itself loads. A surface that decided any of these for
 * itself would be a second home for the fact — and would drift from the audit, which is the one
 * thing a gate must never do.
 *
 * NOT A CONTENT READ: the response carries gate names, reasons and remedies. It never returns the
 * entity's fields. A caller who cannot read the entity is refused instead of answered.
 */
import AuthUtils from "@/providers/firebase/authUtils";
import { ApiClientError, apiClient } from "./apiClient";
import { ApiResponse } from "./kernelBotApi";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";
const GATES_PATH = "/api/v1/pitch/gates";

/** The three gates, in the order the surface shows them. */
export type GateId = "G1" | "G2" | "G3";

export interface GateRemedy {
  /** A stable handle for the action, so a surface can wire a control without parsing prose. */
  action: "create_entity" | "run_extensive_scrape" | "mint_rules" | string;
  label: string;
  how: string;
}

export interface Gate {
  gate: GateId;
  question: string;
  ok: boolean;
  /** WHY the gate answered as it did — so a refusal can be explained, not merely asserted. */
  answer: string;
  /** Present on every refusal, and null on every pass. */
  remedy: GateRemedy | null;
}

export interface PreparationGates {
  can_prepare: boolean;
  gates: Gate[];
  /** The first failing gate, named — so a surface need not scan the list to find what to say. */
  first_refusal: Gate | null;
}

/**
 * Ask whether preparation may start for one entity.
 *
 * A failed gate is NOT an error here: the call answers 200 with `can_prepare: false`, and the
 * gate that refused arrives in `data`. Only a transport or auth failure rejects.
 */
export async function fetchPreparationGates(
  entityId: string,
  timeoutMs = 30_000,
): Promise<ApiResponse<PreparationGates>> {
  const headers = await AuthUtils.getAuthHeaders();
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`${API_URL}${GATES_PATH}`, {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ entity_id: entityId }),
      signal: controller.signal,
    });
    return (await res.json()) as ApiResponse<PreparationGates>;
  } finally {
    window.clearTimeout(timeoutId);
  }
}

/** One prepared item as the pitch QUEUE returns it: the artefact, its report, and the receipt. */
export interface PitchQueueItem {
  item_id: string;
  state: string;
  created_at: string | null;
  actions: string[];
  content: {
    artefact_kind?: string;
    label?: string;
    artefact_text?: string;
    report?: {
      headline?: string;
      readiness?: string;
      top_fixes?: string[];
      counts?: Record<string, number>;
      rules?: Array<{ n: number; verdict: string; why?: string; fix?: string }>;
    };
    provenance?: {
      kernel_key?: string;
      rules_expected?: number;
      model?: string;
      latency_ms?: number;
      checked_at?: string;
    };
  };
}

export interface PitchQueue {
  entity_id: string;
  count: number;
  items: PitchQueueItem[];
}

async function post<T>(path: string, body: unknown, timeoutMs: number): Promise<ApiResponse<T>> {
  const headers = await AuthUtils.getAuthHeaders();
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`${API_URL}${path}`, {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    return (await res.json()) as ApiResponse<T>;
  } finally {
    window.clearTimeout(timeoutId);
  }
}

// The mirror of `post`, for the one read this file does (the deliverables menu). A GET because a
// menu is not a mutation; the auth headers are fetched the same way, so a signed-out surface is
// refused by the same path it would be on any other call here.
async function get<T>(path: string, timeoutMs: number): Promise<ApiResponse<T>> {
  const headers = await AuthUtils.getAuthHeaders();
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`${API_URL}${path}`, { method: "GET", headers, signal: controller.signal });
    return (await res.json()) as ApiResponse<T>;
  } finally {
    window.clearTimeout(timeoutId);
  }
}

/**
 * The artefacts awaiting a decision, with the report behind each one.
 *
 * The SAME definition the Decide lane counts (`awaiting_query`, narrowed by kind), so the number on
 * the door and the items behind it cannot disagree — the defect T1029 already paid for once.
 */
export async function fetchPitchQueue(
  entityId: string,
  timeoutMs = 30_000,
): Promise<ApiResponse<PitchQueue>> {
  return post<PitchQueue>("/api/v1/pitch/queue", { entity_id: entityId }, timeoutMs);
}

/**
 * ONE decision on ONE artefact.
 *
 * `approve` and `reject` are the only accepted verbs: `opened` is a VIEW and the server refuses it,
 * because a queue empty on every look is a queue nobody can answer. A refusal arrives with the
 * machine's own reason code (`terminal_state`, `illegal_transition`, `decider_required`) rather than
 * a bare failure, so the surface can say WHICH rule applied.
 */
export async function decidePitchItem(
  entityId: string,
  itemId: string,
  decision: "approve" | "reject",
  timeoutMs = 30_000,
): Promise<ApiResponse<{ item_id: string; state: string; decided_by: string }>> {
  return post("/api/v1/pitch/item/decide",
    { entity_id: entityId, item_id: itemId, decision }, timeoutMs);
}

/**
 * THE DELIVERABLES MENU — what we provide for a fundraise, and what it costs (T0501 · D-280-007).
 *
 * THE SURFACE COMPUTES NOTHING FROM THIS. Each row's `request` block is declared by the server:
 * its `mode` says whether there is anything to ask for, and a `manual` row carries its price and
 * its channel THERE — so a surface physically cannot render an instant affordance for work a person
 * does, and cannot render a price it invented. That is `D-280-007(b)` expressed as a data shape
 * rather than as a rule the FE has to remember.
 */
export type DeliverableRequestMode = "manual" | "instant" | "none";

export interface DeliverableRequest {
  mode: DeliverableRequestMode;
  /** Present on a `manual` row — what she owes, shown BEFORE she asks. */
  price_usd: number | null;
  /** Present on a `manual` row — the door she asks by. */
  channel: string | null;
  label: string;
}

export interface Deliverable {
  number: number;
  name: string;
  rung: string;
  /** `built` · `manual` · `not_built` — the honest column, and "not built" is a state, not a fault. */
  state: "built" | "manual" | "not_built";
  owner: string;
  price_usd: number | null;
  channel: string | null;
  note: string;
  request: DeliverableRequest;
}

export interface DeliverablesMenu {
  rows: Deliverable[];
  requestable: Deliverable[];
  /** The row the PO named as requestable today (`IN-12`), for a surface that wants to lead with it. */
  requestable_today: number;
  priced_today: { deliverable: number; price_usd: number; channel: string };
}

export async function fetchDeliverables(timeoutMs = 30_000): Promise<ApiResponse<DeliverablesMenu>> {
  return get<DeliverablesMenu>("/api/v1/pitch/deliverables", timeoutMs);
}

/**
 * Ask for one deliverable. The server records the ask; a PERSON does the work.
 *
 * The acceptance carries the price and the channel, so the confirmation this replaces has been TOLD
 * what it is confirming rather than guessing. A refusal names its own cause (`not_requestable`,
 * `unknown_deliverable`, `missing_entity`) instead of failing silently.
 */
export async function requestDeliverable(
  entityId: string,
  deliverable: number,
  note = "",
  timeoutMs = 30_000,
): Promise<ApiResponse<DeliverableRequestResult>> {
  return post("/api/v1/pitch/deliverable/request",
    { entity_id: entityId, deliverable, note }, timeoutMs);
}

export interface DeliverableRequestResult {
  ok: boolean;
  deliverable: number;
  name: string;
  mode: DeliverableRequestMode;
  price_usd: number | null;
  channel: string | null;
  entity_id: string;
  note: string;
  sentence: string;
  item_id: string | null;
  recorded: boolean;
  record_warning?: string;
}

// ══════════════════════════════════════════════════════════════════════════════════════════════
// THE SHAREABLE REVIEW LINK (P280 T0301 · threat T-3)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
// ⚠️ **THE LAST FUNCTION IN THIS FILE IS THE ONLY UNAUTHENTICATED ONE**, and that is the whole feature:
// an outside reader holds a link and nothing else. It goes through `apiClient` with
// `{ anonymous: true }` — the estate's ONE request wrapper and the one place "send no Authorization
// header at all" is decided (`apiClient`'s own docstring; `feedbackApi` is the precedent). The
// authenticated calls below use this file's local `get`/`post`, which attach the Firebase token, so the
// two halves are deliberately different and the difference is the point rather than an inconsistency.

export interface PitchReviewLink {
  link_id: string;
  token?: string;
  path?: string;
  url?: string;
  expires_at?: string | null;
  sentence?: string;
}

export interface PitchReviewLinkRow {
  link_id: string;
  item_id: string;
  state: "live" | "revoked" | "expired";
  created_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  open_count: number;
  last_opened_at: string | null;
}

/** Open a door onto ONE artefact. The link it returns is the only way in. */
export async function createPitchReviewLink(
  entityId: string,
  itemId: string,
  timeoutMs = 30_000,
): Promise<ApiResponse<PitchReviewLink>> {
  return post<PitchReviewLink>("/api/v1/pitch/review-link",
    { entity_id: entityId, item_id: itemId }, timeoutMs);
}

/** The doors she opened, and whether anyone walked through (`open_count`). */
export async function fetchPitchReviewLinks(
  entityId: string,
  timeoutMs = 30_000,
): Promise<ApiResponse<{ entity_id: string; links: PitchReviewLinkRow[] }>> {
  return post("/api/v1/pitch/review-links", { entity_id: entityId }, timeoutMs);
}

/** Close one door. Idempotent: a second press is not a new fact. */
export async function revokePitchReviewLink(
  entityId: string,
  linkId: string,
  timeoutMs = 30_000,
): Promise<ApiResponse<Record<string, unknown>>> {
  return post("/api/v1/pitch/review-link/revoke",
    { entity_id: entityId, link_id: linkId }, timeoutMs);
}

/** What an OUTSIDE reader is sent — the artefact's own facts, and nothing about the entity. */
export interface SharedReview {
  artefact_kind?: string;
  label?: string;
  artefact_text?: string;
  report?: {
    counts?: Record<string, number>;
    readiness?: string;
    [key: string]: unknown;
  };
  provenance?: Record<string, unknown>;
}

/**
 * ⚠️ THE ONE PUBLIC CALL — no principal, no session, no account.
 *
 * Throws `ApiClientError` on a refusal, and the status is what the page renders: **403** is a link
 * that is not open (revoked, expired, forged — the server names the reason in the body but the page
 * only needs to say the link is closed), and **404** is a link whose artefact is gone. The server
 * answers both without telling a stranger anything about the entity, which is why the page can
 * forward its sentence and nothing else.
 */
export async function fetchSharedReview(token: string): Promise<SharedReview> {
  const res = await apiClient<{ status?: boolean; statusCode?: number; message?: string; data?: SharedReview }>(
    "/api/v1/pitch/review",
    { method: "POST", anonymous: true, body: JSON.stringify({ token }) },
  );
  // ⚠️ **THE BODY CARRIES THE VERDICT, NOT THE TRANSPORT** — and getting this wrong told a reader their
  // review was EMPTY when it had been REFUSED. Measured in a browser, 2026-10-12: with a forged token a
  // revoked/forged link rendered *"A shared review · (the artefact text is not on this share) · No rule
  // failed on this text"* — a confident, wrong page — while the BE had answered exactly right.
  //
  // WHY: `apiClient` rejects on a non-2xx, and **every pitch route answers HTTP 200 with the code in
  // `statusCode`** — this project's own T0401(bis) law, which the BE honours and which an FE helper
  // written to the transport's shape silently inverts. So the refusal is read from the body here, the
  // one place that can see both halves, and thrown as the SAME `ApiClientError` an HTTP failure would
  // produce — so every caller's existing handling is correct without knowing this happened.
  const body = res as { status?: boolean; statusCode?: number; message?: string; data?: SharedReview };
  if (body?.status !== true) {
    const code = body?.statusCode ?? 403;
    throw new ApiClientError(
      body?.message || "This review link is not open.",
      code,
      body,
    );
  }
  return body.data ?? {};
}

// ══════════════════════════════════════════════════════════════════════════════════════════════
// THE COMPARISON (P280 T0311 · engine @R36-COMPARE)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
// ⚠️ EVERY CALL HERE IS AUTHENTICATED, unlike the share-link read below them: a comparison is the
// FOUNDER'S own review surface (`D-280-031`), not an outside door. The scoring and the marks are her
// judgements, and a judgement needs a person behind it — the server refuses a mark with no decider.

export interface ComparisonCriterion {
  id: string;
  criterion?: string;
  source?: string;
  role?: "gate" | "contributor" | "multiplier" | "tie-breaker";
  scale?: string;
}

export interface ComparisonVariant {
  id: string;
  pin: string;
  note?: string;
}

export interface ComparisonCell {
  criterion: string;
  variant: string;
  score: number;
  /** The span, the number, or the quoted ABSENCE — never empty; the server refuses a witness-less score. */
  witness: string;
  basis: "observed" | "asserted";
  span?: string;
}

export interface ComparisonMark {
  criterion: string;
  mark: "overruled" | "agreed" | "unclear";
  reason: string;
  decided_by: string;
  decided_at?: string | null;
}

export interface ComparisonVerdict {
  outcome: "decided" | "eliminated";
  reference: string;
  winner?: string;
  decided_by?: string | null;
  direction?: string | null;
  band?: number | null;
  band_note?: string | null;
  profiles?: Record<string, number | null>;
  /** `judged: false` means the gate was NOT JUDGED — a refusal to judge, not a failure. */
  eliminated?: Array<{ variant: string; criterion: string; reason: string; judged?: boolean }>;
  unjudged_gates?: string[];
  survivors?: string[];
  per_criterion?: Array<{ criterion: string; state: string; scores?: Record<string, number> }>;
  sentence: string;
}

export interface ComparisonState {
  comparison_id: string;
  entity_id: string;
  subject: string;
  document_ref: string;
  engine: Record<string, unknown>;
  criteria: ComparisonCriterion[];
  variants: ComparisonVariant[];
  cells: ComparisonCell[];
  marks: ComparisonMark[];
  /** `null` for a variant that FAILED A GATE — eliminated, which is not the same claim as "worse". */
  profiles: Record<string, number | null>;
  verdict: ComparisonVerdict;
  rubric_is_whats_wrong: Array<{ criterion: string; overrules: number; sentence: string }>;
  gate_note: string;
}

/**
 * OPEN A COMPARISON FROM ITS OWN DOCUMENT (`T0318` — the FE half of the entry point).
 *
 * ⚠️ THE ENGINE BLOCK IS PARSED FROM `engineText`, NEVER BESIDE IT, and that is the whole point of the
 * call: the criteria have ONE home (the document) and a caller who could send the block separately could
 * open a comparison against a rubric the document does not carry.
 * ⚠️ A REFUSAL IS A NORMAL ANSWER HERE, NOT AN ERROR TO SWALLOW: an incomplete block answers `400` with
 * **every** problem it found, and the page must show them — a comparison opened on a block that scores
 * cleanly and means nothing is the defect `@R36-COMPARE` §8 exists to forbid.
 */
export async function createComparison(
  entityId: string,
  engineText: string,
  documentRef = "",
  timeoutMs = 30_000,
): Promise<ApiResponse<ComparisonState & { problems?: string[] }>> {
  return post<ComparisonState & { problems?: string[] }>("/api/v1/pitch/comparison",
    { entity_id: entityId, engine_text: engineText, document_ref: documentRef }, timeoutMs);
}

export async function readComparison(
  entityId: string,
  comparisonId: string,
  timeoutMs = 30_000,
): Promise<ApiResponse<ComparisonState>> {
  return post<ComparisonState>("/api/v1/pitch/comparison/read",
    { entity_id: entityId, comparison_id: comparisonId }, timeoutMs);
}

/** Record or CORRECT one cell. The server keeps the hand that moved it and recomputes the verdict. */
export async function scoreComparisonCell(
  entityId: string,
  comparisonId: string,
  cell: { variant: string; criterion: string; score: number; witness: string; basis: "observed" | "asserted" },
  timeoutMs = 30_000,
): Promise<ApiResponse<{ corrected: boolean; cell: ComparisonCell; verdict: ComparisonVerdict }>> {
  return post("/api/v1/pitch/comparison/score",
    { entity_id: entityId, comparison_id: comparisonId, ...cell }, timeoutMs);
}

/** Challenge a CRITERION — a disagreement that outlives the session (`@R36-COMPARE` §6 rule 4). */
export async function markComparisonCriterion(
  entityId: string,
  comparisonId: string,
  mark: { criterion: string; mark: ComparisonMark["mark"]; reason: string },
  timeoutMs = 30_000,
): Promise<ApiResponse<{ marks: ComparisonMark[]; rubric_is_whats_wrong: ComparisonState["rubric_is_whats_wrong"] }>> {
  return post("/api/v1/pitch/comparison/mark",
    { entity_id: entityId, comparison_id: comparisonId, ...mark }, timeoutMs);
}
